import * as React from "react";
import { cn } from "@/lib/utils";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { Loading03Icon } from "@hugeicons/core-free-icons";

type CTAColor =
  | "primary"
  | "secondary"
  | "muted"
  | "green"
  | "cyan"
  | "purple"
  | "pink"
  | "orange"
  | "success"
  | "warning"
  | "error";
type CTAVariant = "default" | "outline" | "ghost" | "icon";

const colorStyles: Record<
  CTAColor,
  {
    bg: string;
    border: string;
    icon: string;
    text: string;
  }
> = {
  primary: {
    bg: "bg-primary/10 hover:bg-primary/20",
    border: "border border-primary/20",
    icon: "text-primary",
    text: "bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent",
  },
  secondary: {
    bg: "bg-muted/50 hover:bg-muted",
    border: "border border-border",
    icon: "text-foreground",
    text: "text-foreground",
  },
  muted: {
    bg: "bg-muted/50 hover:bg-muted",
    border: "border border-border",
    icon: "text-muted-foreground",
    text: "text-muted-foreground",
  },
  green: {
    bg: "bg-accent-green/10 hover:bg-accent-green/20",
    border: "border border-accent-green/20",
    icon: "text-accent-green",
    text: "bg-gradient-to-r from-accent-green to-primary bg-clip-text text-transparent",
  },
  cyan: {
    bg: "bg-accent-cyan/10 hover:bg-accent-cyan/20",
    border: "border border-accent-cyan/20",
    icon: "text-accent-cyan",
    text: "bg-gradient-to-r from-accent-cyan to-primary bg-clip-text text-transparent",
  },
  purple: {
    bg: "bg-accent-purple/10 hover:bg-accent-purple/20",
    border: "border border-accent-purple/20",
    icon: "text-accent-purple",
    text: "bg-gradient-to-r from-accent-purple to-primary bg-clip-text text-transparent",
  },
  pink: {
    bg: "bg-accent-pink/10 hover:bg-accent-pink/20",
    border: "border border-accent-pink/20",
    icon: "text-accent-pink",
    text: "bg-gradient-to-r from-accent-pink to-primary bg-clip-text text-transparent",
  },
  orange: {
    bg: "bg-accent-orange/10 hover:bg-accent-orange/20",
    border: "border border-accent-orange/20",
    icon: "text-accent-orange",
    text: "bg-gradient-to-r from-accent-orange to-primary bg-clip-text text-transparent",
  },
  success: {
    bg: "bg-success/10 hover:bg-success/20",
    border: "border border-success/20",
    icon: "text-success",
    text: "bg-gradient-to-r from-success to-success/80 bg-clip-text text-transparent",
  },
  warning: {
    bg: "bg-warning/10 hover:bg-warning/20",
    border: "border border-warning/20",
    icon: "text-warning",
    text: "bg-gradient-to-r from-warning to-warning/80 bg-clip-text text-transparent",
  },
  error: {
    bg: "bg-error/10 hover:bg-error/20",
    border: "border border-error/20",
    icon: "text-error",
    text: "bg-gradient-to-r from-error to-error/80 bg-clip-text text-transparent",
  },
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: IconSvgElement;
  iconPosition?: "left" | "right";
  color?: CTAColor;
  variant?: CTAVariant;
  size?: "sm" | "md" | "lg" | "xl";
  loading?: boolean;
  children?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      icon,
      iconPosition = "left",
      color = "primary",
      variant = "default",
      size = "sm",
      loading = false,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const colorStyle = colorStyles[color];
    const isIconOnly = variant === "icon";

    const sizeClasses = isIconOnly
      ? {}
      : {
          sm: "gap-1.5 px-2 py-1 text-sm",
          md: "gap-1.5 px-3 py-1.5 text-sm",
          lg: "gap-2 px-4 py-2 text-base",
          xl: "gap-2.5 px-5 py-2.5 text-base",
        };

    const iconSizeClasses = isIconOnly
      ? {
          sm: "h-4 w-4",
          md: "h-5 w-5",
          lg: "h-6 w-6",
        }
      : {
          sm: "h-3.5 w-3.5",
          md: "h-4 w-4",
          lg: "h-4.5 w-4.5",
          xl: "h-5 w-5",
        };

    const variantClasses = {
      default: cn(colorStyle.bg, colorStyle.border),
      outline: cn("bg-transparent hover:bg-muted/50", colorStyle.border),
      ghost: "bg-transparent hover:bg-muted/50 border-transparent",
      icon: "p-2",
    };

    const textClasses = {
      default: colorStyle.text,
      outline: colorStyle.text,
      ghost: colorStyle.text,
    };

    const iconClasses = {
      default: colorStyle.icon,
      outline: colorStyle.icon,
      ghost: colorStyle.icon,
      icon: colorStyle.icon,
    };

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          "group inline-flex cursor-pointer items-center justify-center",
          "rounded-md font-semibold transition-all duration-200",
          "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          variantClasses[variant],
          sizeClasses[size],
          icon && !isIconOnly && "flex-nowrap",
          (disabled || loading) && "pointer-events-none cursor-not-allowed opacity-50",
          className
        )}
        {...props}
      >
        {isIconOnly ? (
          icon ? (
            <>
              <HugeiconsIcon
                icon={loading ? Loading03Icon : icon}
                className={cn(
                  iconSizeClasses[size],
                  iconClasses[variant],
                  "transition-transform group-hover:scale-110",
                  loading && "animate-spin"
                )}
              />
              {children}
            </>
          ) : (
            <>{children}</>
          )
        ) : (
          <>
            {icon && iconPosition === "left" && (
              <HugeiconsIcon
                icon={loading ? Loading03Icon : icon}
                className={cn(
                  iconSizeClasses[size],
                  iconClasses[variant],
                  "shrink-0 transition-transform group-hover:scale-110",
                  loading && "animate-spin"
                )}
              />
            )}
            {children && (
              <span
                className={cn(
                  "inline-flex flex-nowrap items-center space-x-2 font-medium",
                  textClasses[variant]
                )}
              >
                {children}
              </span>
            )}
            {icon && iconPosition === "right" && (
              <HugeiconsIcon
                icon={icon}
                className={cn(
                  iconSizeClasses[size],
                  iconClasses[variant],
                  "shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:scale-110",
                  loading && "animate-spin"
                )}
              />
            )}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };
